import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { http, formatApiError, clearStoredToken } from "@/lib/api";
import { devWarn } from "@/lib/log";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

/**
 * Cookie-only auth. On mount we call /auth/me — the httpOnly `access_token`
 * cookie set by the server determines whether we're signed in.
 * `user` is: null=checking, false=guest, {…}=authenticated.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 while logged out is expected — anything else is worth surfacing (dev only).
      if (err?.response?.status && err.response.status !== 401) {
        devWarn("[auth] /auth/me failed:", err.response.status, err.response.data);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await http.post("/auth/register", { email, password, name });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } catch (err) {
      devWarn("[auth] logout request failed (session cleared client-side):", err?.message);
    }
    clearStoredToken();
    setUser(false);
  }, []);

  // Stable context value — avoids re-rendering every consumer on every parent render.
  const value = useMemo(
    () => ({ user, login, register, logout, refresh, formatApiError }),
    [user, login, register, logout, refresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
